import ReactDOM from 'react-dom/client'
import {
  StrictMode,
  useEffect
} from 'react'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes
} from 'react-router-dom'
import { initializeApp } from 'firebase/app'
import {
  getAuth,
  RecaptchaVerifier
} from 'firebase/auth'
import { getAnalytics } from 'firebase/analytics'
import { getFunctions } from 'firebase/functions'
import { getFirestore } from 'firebase/firestore'
import { getDatabase} from 'firebase/database'
import { Toaster } from 'react-hot-toast'
import AuthProvider, { useAuth } from './providers/auth'
import ThemeProvider, { useTheme } from './providers/theme'
import TranslationFilesProvider from './providers/translationFiles'
import EmbossersProvider from './providers/embossers'
import UsersProvider from './providers/users'
import SparePartsProvider from './providers/spareParts'

import * as io from 'socket.io-client'
import './styles/styles.css'

// ROUTES
import Login from './views/login'
import Forgot from './views/forgot'
import Register from './views/register'
import Account from './views/account'
import Dashboard from './views/dashboard'
import Embossers from './views/embossers'
import Users from './views/users'
import SpareParts from './views/spareParts'
import UnassignedUsers from './views/unassignedUsers'
import UserData from './views/userData'
import Identify from './views/identify'
import Translation from './views/translation'

// DEVELOPMENT SOCKET
// export const socket = io.connect('https://device.indexbraille.com:4002/', { closeOnBeforeunload: false, withCredentials: true })
// STAGING SOCKET
// export const socket = io.connect('https://device.indexbraille.com:4001/', { closeOnBeforeunload: false, withCredentials: true })
// PRODUCTION SOCKET
export const socket = io.connect('https://device.indexbraille.com:4000/', { closeOnBeforeunload: false, withCredentials: true })

declare const window: any

const firebaseConfig = {
  apiKey: 'AIzaSyBiqEKhL80ugf-gjwXkFjRTF_82LldfgIw',
  authDomain: 'index-braille.firebaseapp.com',
  databaseURL: 'https://index-braille-default-rtdb.europe-west1.firebasedatabase.app',
  projectId: 'index-braille',
  storageBucket: 'index-braille.appspot.com',
  messagingSenderId: '1071511074203',
  appId: '1:1071511074203:web:9928a86d30a291390edd3e',
  measurementId: 'G-PXVES1MW8Y'
}

const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const analytics = getAnalytics(app)
export const functions = getFunctions(app)
export const firestore = getFirestore(app)
export const database = getDatabase(app)

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
)

export const routes: {
  path: string
  title: string
  element: JSX.Element
}[] = [
  { path: '/', title: 'Dashboard', element: <Dashboard /> },
  { path: '/account', title: 'Account', element: <Account /> },
  { path: '/embossers', title: 'Embossers', element: <Embossers /> },
  { path: '/users', title: 'Users', element: <Users /> },
  { path: '/spareParts', title: 'Spare parts shop', element: <SpareParts /> },
  { path: '/unassigned-users', title: 'Unassigned Users', element: <UnassignedUsers />},
  { path: '/user-data', title: 'User Data', element: <UserData />},
  { path: '/identify', title: 'Identify', element: <Identify />},
  { path: '/translation', title: 'Translation', element: <Translation />}
]

function App() {
  const {user, firstTimeSignIn, currentRole, multiFactorAuthentication, emailVerified} = useAuth()
  const {mode} = useTheme()

  useEffect(() => {
    (window as any).recaptchaVerifier = new RecaptchaVerifier("recaptcha-container", { size: "invisible" }, auth);
    (window as any).recaptchaVerifier.render()
  }, [])

  useEffect(() => {
    document.documentElement.style.setProperty('--PhoneInputInput-backgroundColor', mode === 'light' ? '#eee' : '#555');
    document.documentElement.style.setProperty('--PhoneInputInput-color', mode === 'light' ? '#252525' : '#f6f6f6');
    document.documentElement.style.setProperty('--PhoneInputCountrySelect-backgroundColor', mode === 'light' ? '#f6f6f6' : '#555');
    document.documentElement.style.setProperty('--PhoneInputCountrySelect-color', mode === 'light' ? '#252525' : '#f6f6f6');
  }, [mode])

  useEffect(() => {
    if (user) {
      const handleTabClose = (event: BeforeUnloadEvent) => {
        socket.emit('set_signed_in', user!.uid, false, multiFactorAuthentication)
        socket.disconnect()
      };

      window.addEventListener('beforeunload', handleTabClose)

      return () => {
        window.removeEventListener('beforeunload', handleTabClose)
      };
    }
  }, [user, multiFactorAuthentication]);

  if (!user) {
    return (
      <div>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot" element={<Forgot />} />
          <Route path="/register" element={<Register />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 3000
          }}
        />
      </div>
    )
  }

  if (firstTimeSignIn) {
    return (
      <div>
        <Routes>
          <Route path='/user-data' element={<UserData />} />
          <Route path='*' element={<Navigate to="/user-data" replace />} />
        </Routes>
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 3000
          }}
        />
      </div>
    )
  }

  if (!emailVerified) {
    return (
      <div>
        <Routes>
          {
            routes.filter(route => route.title === "Dashboard" || route.title === "Account").map(({ path, element }, index) =>
              <Route
                key={index}
                path={path}
                element={element}
              />
            )
          }
        </Routes>
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 3000
          }}
        />
      </div>
    )
  }

  if (currentRole === "Global Administrator") {
    return (
      <div>
        <Routes>
          {
            routes.filter(route => route.title !== "Dashboard").map(({ path, element }, index) =>
              <Route
                key={index}
                path={path}
                element={element}
              />
            )
          }
          <Route
            path="*"
            element={ <Navigate to="/embossers" replace /> }
          />
        </Routes>
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 3000
          }}
        />
      </div>
    )
  }

  // [S.S.] Filtering the routes for each selected role.
  let filteredRoutes = routes.filter(route => route.title === "Account" || route.title === "User Data")
  if (currentRole.includes("User")) {
    for (let route of routes) {
      if (route.title === "Identify") {
        filteredRoutes.push(route)
        break
      }
    }
  }
  if (currentRole.includes("Translator")) {
    for (let route of routes) {
      if (route.title === "Translation") {
        filteredRoutes.push(route)
        break
      }
    }
  }
  if (currentRole.includes("Remote Support") || currentRole.includes("Administrator")) {
    for (let route of routes) {
      if (route.title === "Embossers" || route.title === "Identify") {
        filteredRoutes.push(route)
      }
    }
  }
  if (currentRole.includes("Shop") || currentRole.includes("Administrator")) {
    for (let route of routes) {
      if (route.title === "Spare parts shop" || route.title === "Identify") {
          filteredRoutes.push(route)
      }
    }
  }
  if (currentRole.includes("None")) {
    for (let route of routes) {
      if (route.title === "Dashboard") {
        filteredRoutes.push(route)
        break
      }
    }
  }

  return (
    <div>
      <Routes>
        {
          filteredRoutes.map(({ path, element }, index) =>
            <Route
              key={index}
              path={path}
              element={element}
            />
          )
        }
        <Route
          path="*"
          element={
            currentRole !== undefined && (currentRole.includes("Remote Support") || currentRole.includes("Administrator")) && emailVerified ?
              <Navigate to="/embossers" replace />
            : currentRole !== undefined && currentRole.includes("User") && emailVerified ?
              <Navigate to="/identify" replace />
            : currentRole !== undefined && currentRole.includes("Translator") && emailVerified ?
              <Navigate to="/translation" replace />
            : currentRole !== undefined && currentRole.includes("Shop") && emailVerified ?
              <Navigate to="/spareParts" replace />
            :
              <Navigate to="/" replace />
          }
        />
      </Routes>
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 3000
        }}
      />
    </div>
  )
}

root.render(
  <StrictMode>
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <TranslationFilesProvider>
            <UsersProvider>
              <EmbossersProvider>
                <SparePartsProvider>
                  <div style={{ position: "absolute", zIndex: "9999" }} id="recaptcha-container"></div>
                  <App />
                </SparePartsProvider>
              </EmbossersProvider>
            </UsersProvider>
          </TranslationFilesProvider>
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  </StrictMode>
)